import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, FormControl, Select, MenuItem, IconButton, TextField } from '@mui/material';
import Transition from './common/Transition';
import { renewCollaborateurManually, getCollaborateurById } from '../../../services/CollaborateurService';
import PeriodSelector from './PeriodSelector';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';



const ChangeContractTypeDialog = ({
  open,
  onClose,
  newContratType,
  setNewContratType,
  contratOptions,
  setAddContratOrigin,
  setOpenAddContrat,
  selectedNewContrat,
  newPeriode,
  setNewPeriode,
  newDateFin,
  setNewDateFin,
  getDateFinFromPeriod,
  employeeId,
  snackbarShowMessage,
  setEmployee,
  setReloadNotifications,
  reloadNotifications,
}) => {
  const handleConfirmChange = async () => {
    try {
      const contratType = selectedNewContrat?.toLowerCase();
      const isCDI = contratType === 'cdi';
      const isCDDorFreelance = ['cdd', 'freelance'].includes(contratType);

      const payload = {
        contratId: newContratType,
        dateDebut: new Date().toISOString().split('T')[0],
      };

      if (isCDI) {
        payload.dateFinContrat = null;
        payload.periodeContrat = null;
      }

      if (isCDDorFreelance) {
        payload.dateFinContrat = newDateFin?.toISOString().split('T')[0];

        const debut = new Date(payload.dateDebut);
        const fin = newDateFin;
        const months = (fin.getFullYear() - debut.getFullYear()) * 12 + (fin.getMonth() - debut.getMonth());
        payload.periodeContrat = `${months || 1}mois`;
      }

      await renewCollaborateurManually(employeeId, payload);

      const { data } = await getCollaborateurById(employeeId);
      setEmployee(data);
      setReloadNotifications(!reloadNotifications);

      snackbarShowMessage(
        <span><CheckCircleIcon style={{ verticalAlign: 'middle', marginRight: 6 }} /> Contrat modifié avec succès</span>,
        'success'
      );
      onClose();
    } catch (error) {
      snackbarShowMessage(<span style={{ display: 'flex', alignItems: 'center' }}>
        <ErrorOutlineIcon style={{ color: '#d32f2f', marginRight: 6 }} />Erreur: ${error.message} </span>, 'error');
    }
  };



  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      slotProps={{ paper: {
        sx: {
          borderRadius: 6,
          background: 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        }
      } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontFamily: `'Orbitron', 'Segoe UI', sans-serif`,
          fontWeight: 700,
          fontSize: '1.3rem',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          color: '#0f172a',
          pt: 4,
        }}
      >
        Changer le type de contrat
      </DialogTitle>

      <DialogContent sx={{ px: 4, pt: 2, pb: 3 }}>
        <Box mb={3}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <Typography variant="subtitle2" sx={{ mr: 1 }}>TYPE DE CONTRAT *</Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setAddContratOrigin('changeContratDialog');
                setOpenAddContrat(true);
              }}
              style={{ padding: '4px' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </div>

          <FormControl fullWidth>
            <Select
              value={newContratType}
              onChange={e => {
                const contratId = e.target.value;
                setNewContratType(contratId);
                const selected = contratOptions.find(c => c.id === contratId);

                setNewPeriode('');
                setNewDateFin(null);

                if (selected?.nom_contrat.toLowerCase() === 'cdi') {
                  setNewPeriode('3mois');
                } else if (selected?.nom_contrat.toLowerCase() === 'stage') {
                  setNewPeriode('6mois');
                }
              }}
            >
              <MenuItem value="">Sélectionner...</MenuItem>
              {contratOptions.map(c => (
                <MenuItem
                  key={c.id}
                  value={c.id}
                  sx={{
                    backgroundColor: newContratType === c.id ? '#e0f2fe' : 'inherit',
                    fontWeight: newContratType === c.id ? '600' : 'normal',
                    '&:hover': { backgroundColor: '#1b6c9e' },
                  }}
                >
                  {c.nom_contrat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box mb={2}>
          <Typography variant="subtitle2">DATE DE DÉBUT</Typography>
          <TextField
            fullWidth
            value={new Date().toISOString().split('T')[0]}
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc',
                borderRadius: '10px',
                paddingX: '10px',
                fontFamily: `'Segoe UI', Roboto, sans-serif`,
                boxShadow: 'inset 0 0 0px 1px #e2e8f0',
              }
            }}
          />
        </Box>

        {newContratType && (
          <>
            {selectedNewContrat?.toLowerCase() === 'stage' && (
              <Box mb={2}>
                <Typography variant="subtitle2">PÉRIODE</Typography>
                <PeriodSelector
                  selectedPeriod={newPeriode}
                  onSelectPeriod={period => {
                    setNewPeriode(period);
                    setNewDateFin(getDateFinFromPeriod(new Date(), period));
                  }}
                />
              </Box>
            )}

            {['cdd', 'freelance'].includes(selectedNewContrat?.toLowerCase()) && (
              <Box mb={2}>
                <Typography variant="subtitle2">DATE DE FIN</Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={newDateFin}
                    onChange={(newValue) => setNewDateFin(newValue)}
                    inputFormat="yyyy-MM-dd"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: '10px',
                            paddingX: '10px',
                            fontFamily: `'Segoe UI', Roboto, sans-serif`,
                            boxShadow: 'inset 0 0 0px 1px #e2e8f0',
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Box>
            )}

          </>
        )}

      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'center',
          gap: 2,
          px: 3,
          pb: 4,
          flexWrap: 'wrap'
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '20px',
            px: 3,
            py: 1,
            color: '#c026d3',
            borderColor: '#c026d3',
            '&:hover': {
              background: '#fce7f3',
              borderColor: '#a21caf',
            },
          }}
        >
          Annuler
        </Button>

        <Button
          variant="contained"
          disabled={!newContratType}
          onClick={handleConfirmChange}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '20px',
            px: 3,
            py: 1,
            background: 'linear-gradient(90deg, #3b82f6, #03407b)',
            color: '#fff',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            '&:hover': {
              background: 'linear-gradient(90deg, #2563eb, #02357a)',
            },
          }}
        >
          Confirmer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeContractTypeDialog;